import { faApple } from '@fortawesome/free-brands-svg-icons';
import { type ReactElement } from 'react';
import { useSignInWithApple } from 'react-firebase-hooks/auth';

import { Button, type ButtonVariant, Icon } from '@/components';
import { logEvent, useLogBigQueryEvent } from '@/features/analytics';
import { getNames } from '@/features/auth';
import { useExperiment } from '@/features/experiment';
import { auth } from '@/providers/firebase';

export function AppleLogin({
  source,
  variant,
  className,
  iconClassName,
}: {
  source: 'register' | 'login';
  variant?: ButtonVariant;
  className?: string;
  iconClassName?: string;
}): ReactElement {
  const [signInWithApple] = useSignInWithApple(auth);
  const { logBigQueryEvent } = useLogBigQueryEvent();
  const { experiment } = useExperiment();

  const handleLogin = async (): Promise<void> => {
    const credentials = await signInWithApple();

    if (credentials === undefined) {
      return;
    }

    const eventData: Record<string, string | null> = {
      email: credentials.user.email,
      external_id: credentials.user.uid,
    };

    if (credentials.user.displayName !== null) {
      const userNames = getNames(credentials.user.displayName);

      if (userNames === null) {
        return;
      }

      eventData.first_name = userNames.firstName;
      eventData.last_name = userNames.lastName;
    }

    if ('dataLayer' in window) {
      window.dataLayer.push(eventData);
    }

    logEvent('btn_login_continue_apple');
    logBigQueryEvent(`btn_${source}_continue_apple`);

    if (source === 'register') {
      logBigQueryEvent('sign_up', {
        type: 'apple',
      });
      logEvent('sign_up', {
        type: 'apple',
      });
    }
  };

  return (
    <Button
      size="large"
      variant={
        variant ??
        (experiment === 'alternative-flow' ? 'white-outline' : 'secondary')
      }
      type="button"
      onClick={handleLogin}
      className={className}
    >
      <Icon
        className={iconClassName}
        icon={faApple}
        size={experiment === 'alternative-flow' ? 'xl' : undefined}
      />
      Continue with Apple
    </Button>
  );
}
